import React, { useRef, useState, useEffect } from 'react';
import './WhatsApp.css';
import { X, Plus, Trash2, Send, Image, Type, XSquare, Link, ArrowDownIcon, ArrowUpIcon, RefreshCw, Search } from 'lucide-react';
import { whatsappAPI } from '../../../config/whatsapp-api';
import { toast } from 'react-toastify';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';
import { helpers } from '../../../utils/helpers';

const WhatsApp = ({ adminData }) => {
  const canCreate = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_WHATSAPP_QUE);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingMoreTemplates, setLoadingMoreTemplates] = useState(false);
  const [nextCursor, setNextCursor] = useState('none');
  const [hasMoreTemplates, setHasMoreTemplates] = useState(true);
  const [selectedTemplateName, setSelectedTemplateName] = useState('');

  const [formData, setFormData] = useState({
    templateName: '',
    headerType: 'none',
    headerText: '',
    headerImage: null,
    csvFile: null,
    variables: [],
    userData: [],
    buttons: []
  });
  const [csvError, setCsvError] = useState('');
  const [csvSuccess, setCsvSuccess] = useState('');
  const fileInputRef = useRef(null);

  const [todayQues, setTodayQues] = useState([]);
  const [pastQues, setPastQues] = useState([]);
  const [isPastExpanded, setIsPastExpanded] = useState(false);
  const [refreshingQueId, setRefreshingQueId] = useState(null);
  const [sendingQue, setSendingQue] = useState(false);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrev: false
  });

  const [loading, setLoading] = useState(true);
  const [isPastLoading, setIsPastLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [statsModal, setStatsModal] = useState({
    isOpen: false,
    queId: null,
    queName: null,
    loading: false,
    data: null,
    error: null
  });

  useEffect(() => {
    fetchQues(currentPage);
  }, [currentPage, isPastExpanded]);

  const fetchQues = async (page = 1) => {
    try {
      if (isPastExpanded) {
        setIsPastLoading(true);
      }

      const todayResponse = await whatsappAPI.fetchAllQues({
        type: 'today'
      });

      let pastResponse = null;
      if (isPastExpanded) {
        pastResponse = await whatsappAPI.fetchAllQues({
          type: 'past',
          page: page,
          limit: 10,
        });
      }

      if (todayResponse && todayResponse.success) {
        setTodayQues(todayResponse.data.ques || []);
      } else {
        console.error('Today Ques API Error:', todayResponse?.msg || 'Unknown error');
        setTodayQues([]);
      }

      if (pastResponse && pastResponse.success) {
        setPastQues(pastResponse.data.ques || []);
        setPagination(pastResponse.data.pagination);
      } else if (isPastExpanded) {
        console.error('Past Ques API Error:', pastResponse?.msg || 'Unknown error');
        setPastQues([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          hasNext: false,
          hasPrev: false
        });
      }

    } catch (error) {
      console.error('Error fetching ques:', error);
      setTodayQues([]);
      setPastQues([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        hasNext: false,
        hasPrev: false
      });
    } finally {
      setLoading(false);
      setIsPastLoading(false);
    }
  };

  const fetchTemplates = async (cursor = 'none', more = false) => {
    if (more) {
      setLoadingMoreTemplates(true);
    } else {
      setLoadingTemplates(true);
    }

    try {
      const response = await whatsappAPI.fetchTemplates(cursor);

      if (response.success && response.data.templates) {
        if (more) {
          setTemplates(prev => [...prev, ...response.data.templates]);
        } else {
          setTemplates(response.data.templates);
        }

        if (response.data.nextCursor) {
          setNextCursor(response.data.nextCursor);
          setHasMoreTemplates(true);
        } else {
          setNextCursor(null);
          setHasMoreTemplates(false);
        }
      } else {
        if (!more) setTemplates([]);
        setNextCursor(null);
        setHasMoreTemplates(false);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      if (!error.message.includes('No more templates')) {
        toast.error('Failed to load templates');
      }
      if (!more) setTemplates([]);
      setHasMoreTemplates(false);
    } finally {
      if (more) {
        setLoadingMoreTemplates(false);
      } else {
        setLoadingTemplates(false);
      }
    }
  };

  const handleRefreshQue = async (queId) => {
    try {
      setRefreshingQueId(queId);
      const response = await whatsappAPI.fetchQueById(queId);

      if (response.success) {
        setTodayQues(prev =>
          prev.map(q => q._id === queId ? {
            ...q,
            ...response.data,
          } : q)
        );

        setPastQues(prev =>
          prev.map(q => q._id === queId ? {
            ...q,
            ...response.data,
          } : q)
        );
      }
    } catch (error) {
      console.error('Error refreshing que:', error);
    } finally {
      setRefreshingQueId(null);
    }
  };

  const fetchQueStats = async (queId, queName) => {
    setStatsModal({
      isOpen: true,
      queId,
      queName,
      loading: true,
      data: null,
      error: null
    });

    try {
      const response = await whatsappAPI.fetchQueStats(queId);

      if (response.success) {
        setStatsModal(prev => ({
          ...prev,
          loading: false,
          data: response.data
        }));
      } else {
        setStatsModal(prev => ({
          ...prev,
          loading: false,
          error: response.msg || 'Failed to fetch statistics'
        }));
      }
    } catch (error) {
      setStatsModal(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to fetch statistics'
      }));
    }
  };

  const handleLoadMoreTemplates = () => {
    if (nextCursor && nextCursor !== 'none' && hasMoreTemplates && !loadingMoreTemplates) {
      fetchTemplates(nextCursor, true);
    }
  };

  const handleNextPage = () => {
    if (pagination.hasNext) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (pagination.hasPrev) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleTogglePast = () => {
    const newExpandedState = !isPastExpanded;
    setIsPastExpanded(newExpandedState);

    if (newExpandedState) {
      setCurrentPage(1);
      setPastQues([]);
    } else {
      setPastQues([]);
    }
  };

  const handleSendWhatsApp = async () => {
    setIsTemplateModalOpen(true);
    await fetchTemplates();
  };

  const handleTemplateClick = async () => {
    setIsModalOpen(false);
    setIsTemplateModalOpen(true);
    if (templates.length === 0) {
      await fetchTemplates();
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({
      templateName: '',
      headerType: 'none',
      headerText: '',
      headerImage: null,
      bodyText: '',
      csvFile: null,
      variables: [],
      userData: [],
      buttons: []
    });
    setCsvError('');
    setCsvSuccess('');
  };

  const handleCloseTemplateModal = () => {
    setIsTemplateModalOpen(false);
    setSelectedTemplateName('');
    setTemplates([]);
    setNextCursor('none');
    setHasMoreTemplates(true);
  };

  const handleProceedWithTemplate = () => {
    if (!selectedTemplateName) {
      toast.error('Please select a template');
      return;
    }

    const selectedTemplate = templates.find(temp => temp.name === selectedTemplateName);

    if (selectedTemplate) {
      setFormData(prev => ({
        ...prev,
        templateName: selectedTemplate.name,
        headerType: selectedTemplate.headerType || 'none',
        variables: Array.from({ length: selectedTemplate.body.variables || 0 }, (_, i) => ({
          id: Date.now() + i,
          value: ''
        })),
        buttons: selectedTemplate.buttons || []
      }));
    }

    setIsTemplateModalOpen(false);
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addVariable = () => {
    if (formData.variables.length >= 10) {
      toast('Maximum 10 variables allowed');
      return;
    }

    setFormData(prev => ({
      ...prev,
      variables: [...prev.variables, { id: Date.now(), value: '' }]
    }));
  };

  const removeVariable = (id) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.filter(v => v.id !== id)
    }));
  };

  const updateVariable = (id, value) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.map(v => v.id === id ? { ...v, value } : v)
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 600 * 1024) {
        toast.error('Image size must be less than 600KB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData({
          ...formData,
          headerImage: file,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      headerImage: null
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setCsvError('');
    setCsvSuccess('');

    if (!file) {
      setFormData(prev => ({
        ...prev,
        csvFile: null
      }));
      return;
    }

    if (!file.name.endsWith('.csv')) {
      setCsvError('Please upload a CSV file only');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());

      if (lines.length === 0) {
        setCsvError('CSV file is empty');
        e.target.value = '';
        return;
      }

      const firstLine = lines[0];
      let delimiter = ',';
      if (firstLine.includes('\t')) {
        delimiter = '\t';
      } else if (firstLine.includes(';')) {
        delimiter = ';';
      } else if (!firstLine.includes(',') && /\s{2,}/.test(firstLine)) {
        delimiter = /\s+/;
      }

      const header = typeof delimiter === 'string'
        ? lines[0].toLowerCase().split(delimiter).map(h => h.trim())
        : lines[0].toLowerCase().split(delimiter).map(h => h.trim());

      const hasPhoneColumn = header.some(h =>
        h === 'phone number' || h === 'phonenumber' || h === 'phone'
      );

      if (!hasPhoneColumn) {
        setCsvError('CSV must contain "phone number" column (found columns: ' + header.join(', ') + ')');
        e.target.value = '';
        return;
      }

      const phoneColumnIndex = header.findIndex(h =>
        h === 'phone number' || h === 'phonenumber' || h === 'phone'
      );

      const nameColumnIndex = header.findIndex(h =>
        h === 'name' || h === 'full name' || h === 'fullname'
      );

      const userCount = lines.length - 1;
      if (userCount === 0) {
        setCsvError('CSV file contains no user data');
        e.target.value = '';
        return;
      }

      if (userCount > 15000) {
        setCsvError(`CSV contains ${userCount} users. Maximum 15000 users allowed`);
        e.target.value = '';
        return;
      }

      const invalidEntries = [];
      let validPhoneCount = 0;
      const userData = [];

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        const columns = typeof delimiter === 'string'
          ? line.split(delimiter).map(col => col.trim())
          : line.split(delimiter).map(col => col.trim());

        const phone = columns[phoneColumnIndex] || '';
        const name = nameColumnIndex !== -1 ? (columns[nameColumnIndex] || '') : '';

        if (!phone) {
          invalidEntries.push({
            lineNumber: i + 1,
            phone: '(empty)',
            reason: 'Phone number is empty'
          });
        } else if (validatePhoneNumber(phone)) {
          validPhoneCount++;
          userData.push({
            phone: phone.replace(/\D/g, ''),
            name: name || ''
          });
        } else {
          const newPhone = phone.replace(/\D/g, '');
          const reason = newPhone.length === 0 ?
            'Phone number contains no digits' :
            newPhone.length !== 8 ?
              `Phone number must be exactly 8 digits (found ${newPhone.length})` :
              'Phone number contains invalid characters';

          invalidEntries.push({
            lineNumber: i + 1,
            phone: phone,
            reason: reason
          });
        }
      }

      if (invalidEntries.length > 0) {
        const errorLines = invalidEntries.slice(0, 5).map(entry =>
          `Line ${entry.lineNumber}: ${entry.phone} (${entry.reason})`
        ).join('\n');
        const moreErrors = invalidEntries.length > 5 ?
          `\n... and ${invalidEntries.length - 5} more errors` : '';
        setCsvError(`Invalid phone numbers found:\n${errorLines}${moreErrors}`);
        e.target.value = '';
        return;
      }

      if (validPhoneCount === 0) {
        setCsvError('No valid phone numbers found in CSV');
        e.target.value = '';
        return;
      }

      const nameInfo = nameColumnIndex !== -1 ? ' with names' : '';
      setCsvSuccess(`Successfully loaded ${validPhoneCount} valid user${validPhoneCount !== 1 ? 's' : ''}${nameInfo}`);

      setFormData(prev => ({
        ...prev,
        csvFile: file,
        userData: userData
      }));
    };

    reader.onerror = () => {
      setCsvError('Error reading CSV file');
      e.target.value = '';
    };

    reader.readAsText(file);
  };

  const validatePhoneNumber = (phone) => {
    const newPhone = phone.replace(/\D/g, '');
    return /^\d{8}$/.test(newPhone);
  };

  const removeFile = () => {
    setFormData(prev => ({
      ...prev,
      csvFile: null
    }));
    setCsvError('');
    setCsvSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addButton = () => {
    if (formData.buttons.length >= 3) {
      toast.error('Maximum 3 buttons allowed');
      return;
    }
    setFormData(prev => ({
      ...prev,
      buttons: [...prev.buttons, {
        type: 'url',
        copyText: ''
      }]
    }));
  };

  const removeButton = (index) => {
    setFormData(prev => ({
      ...prev,
      buttons: prev.buttons.filter((btn, i) => i !== index)
    }));
  };

  const updateButton = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      buttons: prev.buttons.map((btn, i) =>
        i === index ? { ...btn, [field]: value } : btn
      )
    }));
  };

  const openStatsModal = (que) => {
    if (que.status === 'completed') {
      setStatsModal({
        isOpen: true,
        queId: que._id,
        queName: que.templateName,
        loading: false,
        data: null,
        error: null
      });
      fetchQueStats(que._id);
    }
  };

  const closeStatsModal = () => {
    setStatsModal({
      isOpen: false,
      queId: null,
      queName: null,
      loading: false,
      data: null,
      error: null
    });
  };

  const handleSend = async () => {
    if (!formData.csvFile) {
      toast.error('Please add a csv file');
      return;
    }

    if (formData.headerType === 'image' && !formData.headerImage) {
      toast.error('Please upload a header image');
      return;
    }

    if (formData.variables.length > 0) {
      const emptyVariables = formData.variables.filter(v => !v.value.trim());
      if (emptyVariables.length > 0) {
        toast.error('Please fill all variables');
        return;
      }
    }

    if (formData.buttons.length > 0) {
      const emptyCopyButtons = formData.buttons.filter(btn => btn.type === 'copy' && !btn.copyText.trim());
      if (emptyCopyButtons.length > 0) {
        toast.error('Please fill copy text for all copy buttons');
        return;
      }
    }

    setSendingQue(true);

    try {
      const userData = formData.userData

      const payload = {
        templateName: formData.templateName,
        headerType: formData.headerType,
        headerText: formData.headerText,
        headerImage: formData.headerImage,
        userData: userData,
        variables: formData.variables,
        buttons: formData.buttons
      };

      const response = await whatsappAPI.createQue(payload);

      if (response.success) {
        toast.success('WhatsApp que created successfully!');
        handleCloseModal();
        fetchQues();
      } else {
        toast.error(`Failed to create que: ${response.msg}`);
      }
    } catch (error) {
      console.error('Error creating whatsapp que:', error);
      toast.error('Error creating whatsapp que. Please try again.');
    } finally {
      setSendingQue(false);
    }
  };

  const queCard = (que) => {
    return (
      <div key={que._id} className={`whatsapp-que-card ${que.status}`} onClick={() => openStatsModal(que)}>
        <div className="whatsapp-que-card-header">
          <h3 className="whatsapp-que-name">{que.templateName}</h3>
          <span className={`whatsapp-que-status ${que.status}`}>
            {que.status === 'in_progress' ? 'In Progress' :
              que.status.charAt(0).toUpperCase() + que.status.slice(1)}
          </span>
          {(que.status === 'in_progress' || que.status === 'pending') && (
            <button
              onClick={() => handleRefreshQue(que._id)}
              disabled={refreshingQueId === que._id}
              className="whatsapp-refresh-btn"
            >
              <RefreshCw size={16} className={refreshingQueId === que._id ? 'spinning' : ''} />
            </button>
          )}
        </div>
        {que.status === 'in_progress' && (
          <div className="whatsapp-que-stats">
            <div className="whatsapp-stat-item">
              <span className="whatsapp-stat-label">Total Count</span>
              <span className="whatsapp-stat-value">{que.totalCount || 0}</span>
            </div>
            <div className="whatsapp-stat-item">
              <span className="whatsapp-stat-label">Success</span>
              <span className="whatsapp-stat-value success">{que.successCount || 0}</span>
            </div>
            <div className="whatsapp-stat-item">
              <span className="whatsapp-stat-label">Failed</span>
              <span className="whatsapp-stat-value failed">{que.failedCount || 0}</span>
            </div>
            <div className="whatsapp-stat-item">
              <span className="whatsapp-stat-label">Progress</span>
              <span className="whatsapp-stat-value">{(que.percentage ?? 0).toFixed(2)}%</span>
            </div>
          </div>
        )}

        {que.status === 'completed' && (
          <div className="whatsapp-que-stats">
            <div className="whatsapp-stat-item">
              <span className="whatsapp-stat-label">Total Count</span>
              <span className="whatsapp-stat-value">{que.totalCount || 0}</span>
            </div>
            <div className="whatsapp-stat-item with-percentage">
              <span className="whatsapp-stat-label">Success</span>
              <span className="whatsapp-stat-value success">{que.successCount || 0}</span>
              <div className="whatsapp-percentage-detail">({(que.successPercentage ?? 0).toFixed(2)}%)</div>
            </div>
            <div className="whatsapp-stat-item with-percentage">
              <span className="whatsapp-stat-label">Failed</span>
              <span className="whatsapp-stat-value failed">{que.failedCount || 0}</span>
              <div className="whatsapp-percentage-detail">({(que.failedPercentage ?? 0).toFixed(2)}%)</div>
            </div>
          </div>
        )}

        {que.status === 'pending' && (
          <div className="whatsapp-que-stats">
            <div className="whatsapp-stat-item">
              <span className="whatsapp-stat-label">Total Count</span>
              <span className="whatsapp-stat-value">{que.totalCount || 0}</span>
            </div>
          </div>
        )}

        <div className="whatsapp-que-dates">
          <div className="whatsapp-que-date">
            Created: {que.createdAt || "-"}
          </div>
          {que.status === 'in_progress' && que.startedAt && (
            <div className="whatsapp-que-date">
              Started: {que.startedAt || "-"}
            </div>
          )}
          {que.status === 'completed' && que.startedAt && (
            <div className="whatsapp-que-date">
              Started: {que.startedAt || "-"}
            </div>
          )}
          {que.status === 'completed' && que.completedAt && (
            <div className="whatsapp-que-date">
              Completed: {que.completedAt || "-"}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="whatsapp-page">
      <div className="whatsapp-page-header">
        <div className="whatsapp-page-title">
          <h1>WhatsApp Ques</h1>
          <p>Send promotional messages and updates to your customers</p>
        </div>
        {canCreate && (
          <button className="whatsapp-send-message-btn" onClick={handleSendWhatsApp}>
            Send Message
          </button>
        )}
      </div>

      <div className="whatsapp-que-section">
        <div className="whatsapp-que-group">
          <h2 className="whatsapp-que-group-title">Today's Ques</h2>
          {loading ? (
            <div className="whatsapp-loading-state">Loading Today's Ques...</div>
          ) : todayQues.length === 0 ? (
            <div className="whatsapp-empty-state">No ques created today</div>
          ) : (
            <div className="whatsapp-que-grid">
              {todayQues.map((que) => queCard(que))}
            </div>
          )}
        </div>

        <div className="whatsapp-past-que-group">
          <div className="whatsapp-past-que-header" onClick={handleTogglePast}>
            <h2 className="whatsapp-que-group-title">Past Ques</h2>
            <button className="whatsapp-toggle-btn">
              {isPastExpanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
            </button>
          </div>

          {isPastExpanded && (
            <>
              {isPastLoading ? (
                <div className="whatsapp-loading-state">Loading Past Ques...</div>
              ) : pastQues.length === 0 ? (
                <div className="whatsapp-empty-state">No past ques found</div>
              ) : (
                <>
                  <div className="whatsapp-que-grid">
                    {pastQues.map((que) => queCard(que))}
                  </div>

                  {pagination.totalPages > 1 && (
                    <div className="whatsapp-pagination">
                      <button
                        onClick={handlePrevPage}
                        disabled={!pagination.hasPrev || isPastLoading}
                        className="whatsapp-pagination-btn"
                      >
                        Previous
                      </button>
                      <span className="whatsapp-pagination-info">
                        Page {pagination.currentPage} of {pagination.totalPages}
                      </span>
                      <button
                        onClick={handleNextPage}
                        disabled={!pagination.hasNext || isPastLoading}
                        className="whatsapp-pagination-btn"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div >

      {statsModal.isOpen && (
        <div className="whatsapp-modal-overlay" onClick={closeStatsModal}>
          <div className="whatsapp-stats-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="whatsapp-stats-modal-header">
              <div>
                <h2 className="whatsapp-stats-modal-title">Statistics</h2>
              </div>
              <button className="whatsapp-stats-close-btn" onClick={closeStatsModal}>×</button>
            </div>

            {statsModal.loading && (
              <div className="whatsapp-stats-loading">
                <div className="whatsapp-stats-loading-spinner"></div>
                <p>Loading statistics...</p>
              </div>
            )}

            {statsModal.error && (
              <div className="whatsapp-stats-error">
                <p className="whatsapp-stats-error-message">{statsModal.error}</p>
              </div>
            )}

            {statsModal.data && (
              <div className="whatsapp-stats-container">
                <div className="whatsapp-stat-item-compact">
                  <div className="whatsapp-stat-header">
                    <span className="whatsapp-stat-label">Messages sent</span>
                    <span className="whatsapp-stat-value">{statsModal.data.totalSent}</span>
                  </div>
                  <div className="whatsapp-stat-bar-container">
                    <div
                      className="whatsapp-stat-bar-fill sent"
                      style={{ width: `${statsModal.data.sentPercentage}%` }}
                    >
                      <span className="whatsapp-stat-percentage">
                        {Math.round(statsModal.data.sentPercentage)}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="whatsapp-stat-item-compact">
                  <div className="whatsapp-stat-header">
                    <span className="whatsapp-stat-label">Messages delivered</span>
                    <span className="whatsapp-stat-value">{statsModal.data.totalDelivered}</span>
                  </div>
                  <div className="whatsapp-stat-bar-container">
                    <div
                      className="whatsapp-stat-bar-fill delivered"
                      style={{ width: `${statsModal.data.deliveredPercentage}%` }}
                    >
                      <span className='whatsapp-stat-percentage'>
                        {Math.round(statsModal.data.deliveredPercentage)}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="whatsapp-stat-item-compact">
                  <div className="whatsapp-stat-header">
                    <span className="whatsapp-stat-label">Messages read</span>
                    <span className="whatsapp-stat-value">{statsModal.data.totalRead}</span>
                  </div>
                  <div className="whatsapp-stat-bar-container">
                    <div
                      className="whatsapp-stat-bar-fill read"
                      style={{ width: `${statsModal.data.readPercentage}%` }}
                    >
                      <span className='whatsapp-stat-percentage'>
                        {Math.round(statsModal.data.readPercentage)}%
                      </span>
                    </div>
                  </div>
                </div>

                {statsModal.data.totalButtonClicks > 0 && (
                  <div className="whatsapp-stat-item-compact">
                    <div className="whatsapp-stat-header">
                      <span className="whatsapp-stat-label">Clicks</span>
                      <span className="whatsapp-stat-value">{statsModal.data.totalButtonClicks}</span>
                    </div>
                    <div className="whatsapp-stat-bar-container">
                      <div
                        className="whatsapp-stat-bar-fill clicks"
                        style={{ width: `${statsModal.data.buttonClicksPercentage}%` }}
                      >
                        <span className='whatsapp-stat-percentage'>
                          {Math.round(statsModal.data.buttonClicksPercentage)}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {statsModal.data.totalErrors > 0 && (
                  <div className="whatsapp-stat-item-compact">
                    <div className="whatsapp-stat-header">
                      <span className="whatsapp-stat-label">Errors</span>
                      <span className="whatsapp-stat-value">{statsModal.data.totalErrors}</span>
                    </div>
                    <div className="whatsapp-stat-bar-container">
                      <div
                        className="whatsapp-stat-bar-fill errors"
                        style={{ width: `${statsModal.data.errorsPercentage}%` }}
                      >
                        <span className='whatsapp-stat-percentage'>
                          {Math.round(statsModal.data.errorsPercentage)}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="whatsapp-stat-divider" />
              </div>
            )}
          </div>
        </div>
      )}

      {isTemplateModalOpen && (
        <div className="whatsapp-modal-overlay">
          <div className="whatsapp-template-modal-simple">
            <div className="whatsapp-modal-header">
              <div>
                <h2 className="whatsapp-modal-title">Select Template</h2>
                <p className="whatsapp-template-subtitle">Choose a template to send messages</p>
              </div>
              <button onClick={handleCloseTemplateModal} className="whatsapp-close-btn">
                <X size={24} />
              </button>
            </div>

            <div className="whatsapp-template-select-section">
              {loadingTemplates ? (
                <div className="whatsapp-loading-state-small">Loading templates...</div>
              ) : templates.length === 0 ? (
                <div className="whatsapp-empty-state-small">No templates available</div>
              ) : (
                <>
                  <div className="whatsapp-template-cards-grid">
                    {templates.map((template) => (
                      <div
                        key={template.name}
                        className={`whatsapp-template-card ${selectedTemplateName === template.name ? 'selected' : ''}`}
                        onClick={() => setSelectedTemplateName(template.name)}
                      >
                        <div className="whatsapp-template-card-header">
                          <h4 className="whatsapp-template-card-name">{template.name}</h4>
                          {selectedTemplateName === template.name && (
                            <div className="whatsapp-template-selected-indicator">✓</div>
                          )}
                        </div>
                        <p className="whatsapp-template-card-body">
                          {template.body?.text?.slice(0, 100) || 'No body text'}
                          {template.body?.text?.length > 100 && '...'}
                        </p>
                      </div>
                    ))}
                  </div>

                  {hasMoreTemplates && (
                    <div className="whatsapp-load-more-container">
                      <button
                        onClick={handleLoadMoreTemplates}
                        disabled={loadingMoreTemplates}
                        className="whatsapp-load-more-btn"
                      >
                        {loadingMoreTemplates ? 'Loading...' : 'Load More Templates'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="whatsapp-template-modal-footer">
              <button
                onClick={handleProceedWithTemplate}
                disabled={!selectedTemplateName || loadingTemplates}
                className={`whatsapp-proceed-btn ${!selectedTemplateName || loadingTemplates ? 'disabled' : ''}`}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="whatsapp-modal-overlay">
          <div className="whatsapp-modal-content">
            <div className="whatsapp-modal-header">
              <div>
                <h2 className="whatsapp-modal-title">
                  Send WhatsApp Message
                </h2>
                <div className="whatsapp-info-section">
                  <div className="whatsapp-info-box">
                    <p>• Use $name in variable for users name</p>
                  </div>
                </div>
              </div>
              <button onClick={handleCloseModal} className="whatsapp-close-btn">
                <X size={24} />
              </button>
            </div>

            <div className="whatsapp-form">
              <div className="whatsapp-form-group">
                <label htmlFor="csvFile">Upload CSV File</label>
                <div className="whatsapp-file-upload-section">

                  <div className="whatsapp-upload-notes">
                    <p className="whatsapp-note-text">CSV must contain column name: "phone number"</p>
                    <p className="whatsapp-note-text">Optional column: "name"</p>
                    <p className="whatsapp-note-text">Phone numbers must be exactly 8 digits</p>
                    <p className="whatsapp-note-text">Don't include country code or spaces</p>
                  </div>

                  <div className="whatsapp-file-upload-row">
                    <input
                      type="file"
                      id="csvFile"
                      name="csvFile"
                      accept=".csv"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                      required
                    />

                    {formData.csvFile && (
                      <button onClick={removeFile} className="whatsapp-remove-file-btn">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>

                  {csvError && (
                    <div className="whatsapp-error-message">
                      {csvError.split('\n').map((line, index) => (
                        <div key={index}>{line}</div>
                      ))}
                    </div>
                  )}

                  {csvSuccess && (
                    <div className="whatsapp-success-message">{csvSuccess}</div>
                  )}
                </div>
              </div>

              <div className="whatsapp-form-group">
                <label>Template Name</label>
                <input
                  type="text"
                  name="templateName"
                  value={formData.templateName}
                  onChange={handleInputChange}
                  onClick={handleTemplateClick}
                  placeholder="Enter template name..."
                  className="whatsapp-input"
                  readOnly={true}
                  style={{ cursor: 'pointer' }}
                />
              </div>

              <div className="whatsapp-section-container">
                <h3 className="whatsapp-section-title">Header</h3>

                <div className="whatsapp-radio-group">
                  {formData.headerType === 'none' &&
                    <label className="whatsapp-radio-option">
                      <input
                        type="radio"
                        name="headerType"
                        value="none"
                        checked={formData.headerType === 'none'}
                        onChange={handleInputChange}
                      />
                      <XSquare size={16} />
                      <span>None</span>
                    </label>
                  }

                  {formData.headerType === 'text' &&
                    <label className="whatsapp-radio-option">
                      <input
                        type="radio"
                        name="headerType"
                        value="text"
                        checked={formData.headerType === 'text'}
                        onChange={handleInputChange}
                      />
                      <Type size={16} />
                      <span>Text</span>
                    </label>
                  }

                  {formData.headerType === 'image' &&
                    <label className="whatsapp-radio-option">
                      <input
                        type="radio"
                        name="headerType"
                        value="image"
                        checked={formData.headerType === 'image'}
                        onChange={handleInputChange}
                      />
                      <Image size={16} />
                      <span>Image</span>
                    </label>
                  }
                </div>

                {formData.headerType === 'text' && (
                  <div className="whatsapp-form-group">
                    <input
                      type="text"
                      name="headerText"
                      value={formData.headerText}
                      onChange={handleInputChange}
                      placeholder="Enter header text..."
                      className="whatsapp-input"
                    />
                  </div>
                )}

                {formData.headerType === 'image' && (
                  <div className="whatsapp-image-upload-section">
                    {!formData.headerImage ? (
                      <div className="whatsapp-image-upload-area">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="whatsapp-image-input"
                          id="header-image"
                        />
                        <label htmlFor="header-image" className="whatsapp-image-upload-label">
                          <Image size={24} />
                          <span>Click to upload header image</span>
                        </label>
                      </div>
                    ) : (
                      <div className="whatsapp-image-preview">
                        <img
                          src={URL.createObjectURL(formData.headerImage)}
                          alt="Header preview"
                          className="whatsapp-preview-image"
                        />
                        <button onClick={removeImage} className="whatsapp-remove-image-btn">
                          <Trash2 size={14} />
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="whatsapp-section-container">
                <h3 className="whatsapp-section-title">Body</h3>

                <div className="whatsapp-form-group">
                  <label>Text</label>
                  <textarea
                    value={templates.find(t => t.name === formData.templateName)?.body.text || ''}
                    className="whatsapp-textarea"
                    rows="6"
                  />
                </div>

                {formData.variables.length > 0 && (
                  <div className="whatsapp-variables-section">
                    <h4 className="whatsapp-subsection-title">Variables</h4>
                    {formData.variables.map((variable, index) => (
                      <div key={variable.id} className="whatsapp-variable-item">
                        <div className="whatsapp-variable-header">
                          <label>Variable {index + 1}</label>
                        </div>
                        <input
                          type="text"
                          value={variable.value}
                          onChange={(e) => updateVariable(variable.id, e.target.value)}
                          placeholder={`{{${index + 1}}}`}
                          className="whatsapp-input"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {formData.buttons && formData.buttons.length > 0 && (
                <div className="whatsapp-section-container">
                  <div className="whatsapp-section-header">
                    <h3 className="whatsapp-section-title">Buttons</h3>
                  </div>

                  {formData.buttons.map((button, index) => (
                    <div key={index} className="whatsapp-button-item">
                      <div className="whatsapp-button-header">
                        <label>Button {index + 1}</label>
                      </div>

                      <div className="whatsapp-button-type-group">
                        <label className="whatsapp-radio-option">
                          <input
                            type="radio"
                            checked={button.type === 'url'}
                            onChange={(e) => updateButton(index, 'type', 'url')}
                          />
                          <Link size={16} />
                          <span>URL</span>
                        </label>

                        <label className="whatsapp-radio-option">
                          <input
                            type="radio"
                            checked={button.type === 'copy'}
                            onChange={(e) => updateButton(index, 'type', 'copy')}
                          />
                          <Type size={16} />
                          <span>Copy Text</span>
                        </label>
                      </div>

                      {button.type === 'copy' && (
                        <div className="whatsapp-form-group">
                          <input
                            type="text"
                            value={button.copyText || ''}
                            onChange={(e) => updateButton(index, 'copyText', e.target.value)}
                            placeholder="Text to copy..."
                            className="whatsapp-input"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={handleSend}
                disabled={
                  !formData.csvFile ||
                  sendingQue ||
                  (formData.headerType === 'image' && !formData.headerImage) ||
                  (formData.variables.length > 0 && formData.variables.some(v => !v.value.trim()))
                }

                className={`whatsapp-send-btn ${!formData.csvFile ||
                  (formData.headerType === 'image' && !formData.headerImage) ||
                  (formData.variables.length > 0 && formData.variables.some(v => !v.value.trim()))
                  ? 'disabled' : ''
                  }`}
              >
                <Send size={18} />
                {sendingQue ? 'Creating...' : 'Send WhatsApp Message'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div >
  );
};

export default WhatsApp;